import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { LENS_OPTION_ROWS, SPECTERA_CLAIM_FORM } from './constants';
import SpecteraLensDropdownRow from './SpecteraLensDropdownRow';

const selector = formValueSelector(SPECTERA_CLAIM_FORM);

const resolveOptionFields = (items = []) => {
  const options = Array.isArray(items) ? items : [];

  const first = options.find(
    (item) => item && typeof item === 'object'
  );

  if (!first) {
    return {
      valueField: 'LENS_OPTION_DESCRIPTION',
      textField: 'LENS_OPTION_DESCRIPTION',
    };
  }

  const possibleTextKeys = [
    'LENS_OPTION_DESCRIPTION',
    'OPTION_DESCRIPTION',
    'LENS_TYPE_DESCRIPTION',
    'LENS_TREATMENT_DESCRIPTION',
    'LENS_STYLE_DESCRIPTION',
    'LENS_MATERIAL_DESCRIPTION',
    'DESCRIPTION',
    'description',
    'NAME',
    'name',
    'label',
    'title',
  ];

  const textField =
    possibleTextKeys.find((key) => key in first) ||
    Object.keys(first)[1] ||
    Object.keys(first)[0] ||
    'LENS_OPTION_DESCRIPTION';

  return { valueField: textField, textField };
};

export default function SpecteraLensOptionsSection({
  data = [],
  formSection,
  loading = false,
}) {
  const dispatch = useDispatch();
  const options = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const { valueField, textField } = useMemo(
    () => resolveOptionFields(options),
    [options]
  );

  // Read all selected lens options from Redux Form.
  const selectedValues = useSelector((state) =>
    LENS_OPTION_ROWS.map((optionNumber) =>
      selector(state, `${formSection}.options_${optionNumber}`)
    )
  );

  useEffect(() => {
    if (options.length > 0) {
      LENS_OPTION_ROWS.forEach((optionNumber, idx) => {
        const val = selectedValues[idx];
        if (val !== null && val !== undefined && val !== '') {
          const matched = options.find(
            (opt) =>
              String(opt.LENS_OPTION_ID) === String(val) ||
              String(opt.OPTION_ID) === String(val) ||
              String(opt.ID) === String(val) ||
              String(opt.id) === String(val) ||
              opt[textField] === val
          );
          if (matched && matched[textField] && val !== matched[textField]) {
            dispatch(
              change(
                SPECTERA_CLAIM_FORM,
                `${formSection}.options_${optionNumber}`,
                matched[textField]
              )
            );
          }
        }
      });
    }
  }, [options, selectedValues, formSection, dispatch, textField]);

  return (
    <>
      <hr />

      <div className="d-flex align-items-center mb-5">
        <h5 className="mb-0">Lens Options</h5>

        {loading && (
          <div className="d-flex align-items-center ml-4">
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            />
            <span className="ml-2 text-muted font-size-sm">
              Loading lens options...
            </span>
          </div>
        )}
      </div>

      <div>
        {LENS_OPTION_ROWS.map((optionNumber, index) => {
          const currentValue = selectedValues[index];

          // Exclude options selected in other dropdowns.
          // Keep the current dropdown's own selected option visible.
          const otherSelectedIds = selectedValues
            .filter((value, selectedIndex) => selectedIndex !== index)
            .filter(
              (value) =>
                value !== null &&
                value !== undefined &&
                value !== ''
            )
            .map((value) =>
              typeof value === 'object'
                ? value[valueField]
                : value
            );

          const filteredOptions = options.filter((option) => {
            const optionId = option[valueField];

            const alreadySelected = otherSelectedIds.some(
              (selectedId) => String(selectedId) === String(optionId)
            );

            const isCurrentSelection =
              currentValue !== null &&
              currentValue !== undefined &&
              currentValue !== '' &&
              String(
                typeof currentValue === 'object'
                  ? currentValue[valueField]
                  : currentValue
              ) === String(optionId);

            return !alreadySelected || isCurrentSelection;
          });

          return (
            <SpecteraLensDropdownRow
              key={`${optionNumber}-${options.length}`}
              name={`options_${optionNumber}`}
              label={`Lens Option ${optionNumber}`}
              valueField={valueField}
              textField={textField}
              billAmountName={`options_${optionNumber}_bill_amount`}
              data={filteredOptions}
              formSection={formSection}
              required={optionNumber === 1}
              fieldKey={`lens-options-${optionNumber}-${options.length}`}
            />
          );
        })}
      </div>
    </>
  );
}
